import { Menu as BaseMenu } from "@base-ui/react/menu";
import checkIcon from "@loot-os/icons/icons/check";
import type { IconData } from "@loot-os/icons/types";
import type { MouseEventHandler, ReactElement, ReactNode } from "react";
import { cn } from "../foundations/cn.js";
import {
  POPUP,
  POPUP_GROUP_LABEL,
  POPUP_ITEM,
  POPUP_MOTION,
  POPUP_SEPARATOR,
} from "../foundations/interaction.js";
import { useLinkRender } from "../foundations/linkContext.js";
import { Icon } from "./Icon.js";

export interface MenuProps {
  /** The button that opens the menu (a Button or IconButton); it gets aria-expanded. */
  trigger: ReactElement;
  /** MenuItems, MenuGroupLabels and MenuSeparators. */
  children: ReactNode;
  align?: "start" | "center" | "end";
  /** Which side of the trigger it opens on; it flips when there is no room. */
  side?: "top" | "bottom";
  defaultOpen?: boolean;
  /** Controlled open state, for a menu that stays open while its radio items are picked. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

/** A list of actions or links opened from a button (`role="menu"`). */
export function Menu({
  trigger,
  children,
  align = "end",
  side = "bottom",
  defaultOpen,
  open,
  onOpenChange,
  className,
}: MenuProps) {
  return (
    <BaseMenu.Root
      defaultOpen={defaultOpen}
      open={open}
      onOpenChange={onOpenChange ? (next) => onOpenChange(next) : undefined}
      modal={false}
    >
      <BaseMenu.Trigger render={trigger} />
      <BaseMenu.Portal>
        <BaseMenu.Positioner side={side} align={align} sideOffset={4} className="z-50 outline-none">
          <BaseMenu.Popup className={cn(POPUP, POPUP_MOTION, "min-w-56", className)}>
            {children}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  );
}

export interface MenuItemProps {
  children: ReactNode;
  icon?: IconData;
  /** Makes the item a link, through the app's LinkProvider or `render`. Disabled, it isn't one. */
  href?: string;
  render?: ReactElement;
  onClick?: MouseEventHandler<HTMLElement>;
  disabled?: boolean;
  /** Red label, for an action that destroys (Log out stays neutral). */
  danger?: boolean;
}

/** One row: a link with `href`, otherwise an action. */
export function MenuItem({
  children,
  icon,
  href,
  render,
  onClick,
  disabled,
  danger,
}: MenuItemProps) {
  const className = cn(
    POPUP_ITEM,
    "cursor-pointer",
    danger && "text-status-danger data-highlighted:text-status-danger",
  );
  const linkRender = useLinkRender(render);
  const content = (
    <>
      {icon && <Icon icon={icon} className="shrink-0" />}
      {children}
    </>
  );
  if (href !== undefined && !disabled) {
    return (
      <BaseMenu.LinkItem
        href={href}
        render={linkRender}
        closeOnClick
        onClick={onClick}
        className={className}
      >
        {content}
      </BaseMenu.LinkItem>
    );
  }
  return (
    <BaseMenu.Item onClick={onClick} disabled={disabled} className={className}>
      {content}
    </BaseMenu.Item>
  );
}

/** A label above a group, e.g. the signed-in name. */
export function MenuGroupLabel({ children }: { children: ReactNode }) {
  return <div className={POPUP_GROUP_LABEL}>{children}</div>;
}

/** A rule between groups. */
export function MenuSeparator() {
  return <BaseMenu.Separator className={POPUP_SEPARATOR} />;
}

export interface MenuRadioGroupProps {
  /** The group's name, shown above its items. */
  label: string;
  /** The checked item's value; one that matches no item checks none. */
  value: string;
  onValueChange: (value: string) => void;
  /** MenuRadioItems. */
  children: ReactNode;
}

/**
 * A named group of choices, the current one checked (`menuitemradio`). Several groups may share
 * one value, so a choice listed under two headings is checked in whichever holds it.
 */
export function MenuRadioGroup({ label, value, onValueChange, children }: MenuRadioGroupProps) {
  return (
    <BaseMenu.RadioGroup
      value={value}
      onValueChange={(next) => onValueChange(String(next))}
      className="flex flex-col"
    >
      <BaseMenu.GroupLabel className="truncate px-2.5 pt-1.5 pb-1 font-body text-body-b3 text-text-tertiary">
        {label}
      </BaseMenu.GroupLabel>
      {children}
    </BaseMenu.RadioGroup>
  );
}

export interface MenuRadioItemProps {
  value: string;
  children: ReactNode;
  /** Closes the menu once picked; off, the menu stays open to compare choices. */
  closeOnClick?: boolean;
}

/** One choice in a MenuRadioGroup: checked by its fill and a check mark, never color alone. */
export function MenuRadioItem({ value, children, closeOnClick = true }: MenuRadioItemProps) {
  return (
    <BaseMenu.RadioItem
      value={value}
      closeOnClick={closeOnClick}
      className={cn(
        POPUP_ITEM,
        "min-w-0 cursor-pointer py-1.5 data-checked:bg-accent-subtle data-checked:text-text-primary",
      )}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2.5">{children}</span>
      <BaseMenu.RadioItemIndicator className="flex shrink-0 text-accent-text">
        <Icon icon={checkIcon} size={16} />
      </BaseMenu.RadioItemIndicator>
    </BaseMenu.RadioItem>
  );
}
